/**
 * InfinitSales - Conexao do Facebook Messenger
 *
 * Clone do InstagramConnectionModal com o PASSO NOVO do canal: o Facebook
 * Login autoriza o USUARIO, que pode administrar varias Paginas — depois que o
 * backend troca o ?code= (connect-oauth), ele devolve a lista de Paginas e
 * este modal renderiza o seletor; o clique numa Pagina chama select-page e
 * finaliza a conta. Tokens nunca chegam ao frontend.
 *
 * O modo teste (colar um Page Access Token do painel da Meta) continua
 * existindo, colapsavel, para destravar clientes antes do App Review.
 *
 * Requisito facil de esquecer, e que a doc da Meta repete: o app precisa estar
 * em modo LIVE para RECEBER webhooks. Publicar nao exige App Review. Sem isso
 * a conexao funciona, o envio funciona, e nenhuma mensagem chega — por isso o
 * aviso esta na tela e nao so na documentacao.
 */
import React, { useState } from 'react';
import {
  X, Facebook, Loader2, ExternalLink, AlertTriangle, KeyRound, ChevronRight,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import messengerService from '../../services/messenger/messengerService';

const MS_BLUE = '#0084ff';

const STEPS = [
  'No painel da Meta (App Dashboard), abra o produto Messenger e vá em "Configurações da API do Messenger".',
  'Em "Gerar tokens de acesso", vincule a Página do cliente e gere o Page Access Token.',
  'Confirme que a Página está assinada nos webhooks do produto (campo messages).',
  'Cole o token abaixo.',
];

// Facebook Login (OAuth de producao). O ID e o do APP FACEBOOK (o mesmo
// META_APP_ID do backend). Scopes: SO os que o produto usa — pedir scope a
// mais reprova o App Review.
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '1833485603931931';
const FB_GRAPH_VERSION = process.env.REACT_APP_META_GRAPH_VERSION || 'v22.0';
const OAUTH_SCOPES = 'pages_messaging,pages_show_list,pages_manage_metadata';

const buildAuthorizeUrl = () => {
  // state anti-CSRF: gerado aqui, conferido na volta pelo shell do inbox.
  const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
  try { sessionStorage.setItem('ms_oauth_state', state); } catch { /* ignore */ }
  const params = new URLSearchParams({
    client_id: FACEBOOK_APP_ID,
    redirect_uri: `${window.location.origin}/messenger`,
    response_type: 'code',
    scope: OAUTH_SCOPES,
    state,
  });
  return `https://www.facebook.com/${FB_GRAPH_VERSION}/dialog/oauth?${params.toString()}`;
};

const MessengerConnectionModal = ({
  onClose,
  onConnected,
  externalError = null,
  // Passo 2 do OAuth: lista de Páginas devolvida pelo connect-oauth (o shell
  // do inbox troca o code e passa o resultado para cá). Com ela presente, o
  // modal vira o seletor de Página.
  pages = null,
}) => {
  const { colors } = useTheme();
  const [token, setToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [selectingPageId, setSelectingPageId] = useState(null);
  const [error, setError] = useState(externalError);
  const [showManual, setShowManual] = useState(false);

  const showPagePicker = Array.isArray(pages) && pages.length > 0;

  const handleConnect = async (e) => {
    e?.preventDefault();
    const value = token.trim();
    if (!value || connecting) return;

    setConnecting(true);
    setError(null);
    try {
      const res = await messengerService.connectManual(value);
      onConnected?.(res?.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === 'object' ? detail.message : detail)
        || 'Não foi possível conectar. Confira o token e tente de novo.'
      );
    } finally {
      setConnecting(false);
    }
  };

  // Passo 2: o clique na Página finaliza a conta. O doc pendente expira em
  // ~15 minutos — se expirou, o erro pede para refazer o login.
  const handleSelectPage = async (pageId) => {
    if (selectingPageId) return;
    setSelectingPageId(pageId);
    setError(null);
    try {
      const res = await messengerService.selectPage(pageId);
      onConnected?.(res?.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === 'object' ? detail.message : detail)
        || 'Não foi possível conectar a Página. Tente de novo.'
      );
    } finally {
      setSelectingPageId(null);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    },
    modal: {
      backgroundColor: colors.bgPrimary, borderRadius: 12, width: '100%',
      maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    header: {
      display: 'flex', alignItems: 'center', gap: 10, padding: '18px 20px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    title: { flex: 1, fontSize: 17, fontWeight: 600, color: colors.textPrimary },
    body: { padding: 20, display: 'flex', flexDirection: 'column', gap: 16 },
    intro: { fontSize: 13, color: colors.textSecondary, lineHeight: 1.55 },
    steps: {
      margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8,
      fontSize: 13, color: colors.textPrimary, lineHeight: 1.5,
    },
    warning: {
      display: 'flex', gap: 10, padding: '12px 14px', borderRadius: 8,
      backgroundColor: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)',
      fontSize: 12.5, color: colors.textPrimary, lineHeight: 1.5,
    },
    label: { fontSize: 13, fontWeight: 600, color: colors.textPrimary },
    input: {
      width: '100%', boxSizing: 'border-box', padding: '11px 14px',
      borderRadius: 8, border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgSecondary, color: colors.textPrimary,
      fontSize: 14, fontFamily: 'monospace', outline: 'none',
    },
    error: {
      padding: '10px 14px', borderRadius: 8, fontSize: 13,
      backgroundColor: 'rgba(239,68,68,0.12)', color: '#dc2626', lineHeight: 1.5,
    },
    footer: {
      display: 'flex', justifyContent: 'flex-end', gap: 10,
      padding: '16px 20px', borderTop: `1px solid ${colors.borderColor}`,
    },
    btnGhost: {
      padding: '10px 18px', borderRadius: 8, border: `1px solid ${colors.borderColor}`,
      backgroundColor: 'transparent', color: colors.textPrimary,
      fontSize: 14, cursor: 'pointer',
    },
    btnPrimary: {
      padding: '10px 20px', borderRadius: 8, border: 'none',
      backgroundColor: MS_BLUE, color: '#fff', fontSize: 14, fontWeight: 600,
      display: 'flex', alignItems: 'center', gap: 8,
    },
    oauthBtn: {
      width: '100%', padding: '13px 20px', borderRadius: 10, border: 'none',
      background: 'linear-gradient(90deg, #1877f2 0%, #0084ff 60%, #00b2ff 100%)',
      color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    },
    manualToggle: {
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'transparent', border: 'none', padding: 0,
      color: colors.textSecondary, fontSize: 12.5, cursor: 'pointer',
      textDecoration: 'underline', alignSelf: 'flex-start',
    },
    docLink: {
      display: 'inline-flex', alignItems: 'center', gap: 4,
      color: colors.accentPrimary, fontSize: 12.5, textDecoration: 'none',
    },
    // Seletor de Página (passo 2 do OAuth)
    pageCard: {
      display: 'flex', alignItems: 'center', gap: 12, width: '100%',
      padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
      border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgSecondary, textAlign: 'left',
    },
    pageAvatar: {
      width: 42, height: 42, borderRadius: '50%', overflow: 'hidden',
      flexShrink: 0, backgroundColor: colors.bgTertiary,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: colors.textTertiary,
    },
    pageName: {
      flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600,
      color: colors.textPrimary, overflow: 'hidden',
      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <Facebook size={22} color={MS_BLUE} />
          <span style={styles.title}>Conectar Messenger</span>
          <X
            size={20}
            color={colors.textSecondary}
            style={{ cursor: 'pointer' }}
            onClick={onClose}
          />
        </div>

        {showPagePicker ? (
          // ============ PASSO 2: escolher a Página ============
          <div style={styles.body}>
            <p style={styles.intro}>
              Autorização concluída. Escolha qual Página do Facebook vai
              receber as conversas do Messenger no InfinitSales:
            </p>

            {error && <div style={styles.error}>{error}</div>}

            {pages.map((p) => (
              <button
                key={p.page_id}
                type="button"
                style={{
                  ...styles.pageCard,
                  opacity: selectingPageId && selectingPageId !== p.page_id ? 0.55 : 1,
                  cursor: selectingPageId ? 'default' : 'pointer',
                }}
                disabled={!!selectingPageId}
                onClick={() => handleSelectPage(p.page_id)}
                onMouseEnter={(e) => {
                  if (!selectingPageId) e.currentTarget.style.borderColor = MS_BLUE;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.borderColor;
                }}
              >
                <span style={styles.pageAvatar}>
                  {p.profile_picture_url ? (
                    <img
                      src={p.profile_picture_url}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Facebook size={18} />
                  )}
                </span>
                <span style={styles.pageName}>{p.page_name || p.page_id}</span>
                {selectingPageId === p.page_id ? (
                  <Loader2
                    size={16}
                    color={MS_BLUE}
                    style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }}
                  />
                ) : (
                  <ChevronRight size={16} color={colors.textTertiary} style={{ flexShrink: 0 }} />
                )}
              </button>
            ))}

            <p style={{ ...styles.intro, fontSize: 12 }}>
              A lista expira em alguns minutos. Se der erro, feche e refaça o
              login com o Facebook.
            </p>
          </div>
        ) : (
          <form onSubmit={handleConnect}>
            <div style={styles.body}>
              {/* Caminho de PRODUCAO: Facebook Login. O clique sai para o
                  Facebook e a Meta volta para /messenger?code=..., que o shell
                  do inbox troca no backend — e então este modal reabre com a
                  lista de Páginas para escolher. */}
              <p style={styles.intro}>
                Entre com o Facebook para conectar o Messenger da sua Página ao
                InfinitSales. Você será levado ao Facebook para autorizar,
                voltará aqui automaticamente e então escolherá a Página.
              </p>

              <button
                type="button"
                style={styles.oauthBtn}
                onClick={() => { window.location.href = buildAuthorizeUrl(); }}
              >
                <Facebook size={18} />
                Entrar com Facebook
              </button>

              {error && <div style={styles.error}>{error}</div>}

              <button
                type="button"
                style={styles.manualToggle}
                onClick={() => setShowManual((v) => !v)}
              >
                <KeyRound size={13} />
                {showManual
                  ? 'Ocultar conexão por token (modo teste)'
                  : 'Conectar com token (modo teste)'}
              </button>

              {showManual && (
                <>
                  <p style={styles.intro}>
                    Enquanto a aprovação do aplicativo não sai, Páginas de teste
                    conectam pelo painel da Meta. São quatro passos:
                  </p>

                  <ol style={styles.steps}>
                    {STEPS.map((step) => <li key={step}>{step}</li>)}
                  </ol>

                  <div style={styles.warning}>
                    <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} color="#d97706" />
                    <span>
                      O aplicativo precisa estar no modo <strong>Live</strong> no painel
                      da Meta para receber mensagens. Publicar o aplicativo não exige
                      aprovação. Sem isso a Página conecta normalmente, mas nenhuma
                      mensagem chega ao inbox.
                    </span>
                  </div>

                  <div>
                    <label style={styles.label} htmlFor="ms-token">Page Access Token</label>
                    <input
                      id="ms-token"
                      style={{ ...styles.input, marginTop: 6 }}
                      placeholder="EAAa..."
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    <div style={{ marginTop: 8 }}>
                      <a
                        style={styles.docLink}
                        href="https://developers.facebook.com/apps/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Abrir o painel da Meta <ExternalLink size={12} />
                      </a>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={styles.footer}>
              <button type="button" style={styles.btnGhost} onClick={onClose}>
                Cancelar
              </button>
              {showManual && (
                <button
                  type="submit"
                  style={{
                    ...styles.btnPrimary,
                    opacity: token.trim() && !connecting ? 1 : 0.6,
                    cursor: token.trim() && !connecting ? 'pointer' : 'default',
                  }}
                  disabled={!token.trim() || connecting}
                >
                  {connecting ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Conectando...</> : 'Conectar'}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default MessengerConnectionModal;
